import { AssignMemberListModal, CommunicationNote } from 'components';
import AudioIconPopupZoom from 'components/common/audioIconPopupZoom';
import { useAuthContext } from 'contexts';
import type { Annotation, Communication, Patient } from 'fhir/r4';
import { FHIRProxyClient, ProvenanceBuilder } from 'fhir-sdoh';
import moment from 'moment';
import React, { useState } from 'react';
import { Button, Icon, Loader, Message, Popup, Table, Modal, Confirm } from 'semantic-ui-react';
import { fhirPut } from 'services';
import type { ZoomCallRecord } from 'services/connect';
import type { CommunicationData, ZoomEmailInlineFile, ZoomEmailMessage } from 'types';
import { CommunicationHelper, ErrorResponse, parseFHIRPatientName, printPrettyJSON, sanitizeEmailHtml } from 'utils';

import * as Connect from '../../../../../services/connect';


type ZoomRowProps = {
  debugCommunication: Communication;
  communicationsMutate: () => Promise<CommunicationData | undefined>;
  handleErrorMessage: (errorMessage: string) => void;
};

const ZoomRow: React.FC<ZoomRowProps> = ({ debugCommunication, communicationsMutate, handleErrorMessage }) => {
  const { practitionerId, practitionerName, token } = useAuthContext();

  const [copyClickError, setCopyClickError] = useState<string>('');
  // Seeded `undefined` but reset with `null` by `handleDeleteCall` and the confirm
  // dialog's cancel, so the state really holds both spellings of absent.
  const [deleteCall, setDeleteCall] = useState<Communication | null | undefined>(undefined);
  const [isCopyClickSuccess, setIsCopyClickSuccess] = useState(false);
  const [isAssignMemberModalOpen, setIsAssignMemberModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // Reset with `null` on close, so the state genuinely holds all three.
  const [selectedCallerCard, setSelectedCallerCard] = useState<Patient | null | undefined>(undefined);
  const [selectedCallTicket, setSelectedCallTicket] = useState<CommunicationHelper | null>(null);
  const [data, setData] = useState<ZoomCallRecord>(null);

  // Extract Engagement Id from identifier (engagement-id)
  const id =
    debugCommunication.identifier?.find(
      (id) => id.system === 'https://projectwell.io/fhir/identifiers/communication/engagement-id'
    )?.value || 'N/A';

  // Extract Sender
  const sender = debugCommunication.sender?.display || 'N/A';

  // Extract Direction (inbound|outbound) from identifier (direction)
  const direction =
    debugCommunication.identifier?.find(
      (ext) => ext.system === 'https://projectwell.io/fhir/identifiers/communication/direction'
    )?.value || 'N/A';

  // Voicemail identifier
  const voicemailIdentifier =
    debugCommunication.identifier?.find(
      (id) => id.system === 'https://projectwell.io/fhir/identifiers/communication/voicemail'
    )?.value === 'true';

  // Transfer identifier
  const transferIdentifier =
    debugCommunication.identifier?.find(
      (id) => id.system === 'https://projectwell.io/fhir/transfer-from-phone-number'
    )?.value;

  //TODO link to recording if voice. Content if email/sms
  const _content = '';
  // Extract Channel from medium
  const channel =
    debugCommunication.medium?.find((med) => med.coding![0]!.system === 'https://projectwell.io/fhir/medium/channel')
      ?.text || 'N/A';

  // Extract Start Time from extension (start-time)
  const startTime =
    debugCommunication.extension?.find((ext) => ext.url === 'https://projectwell.io/fhir/start-time')?.valueString ||
    'N/A';
  // Extract End Time from extension (end-time)
  const endTime =
    debugCommunication.extension?.find((ext) => ext.url === 'https://projectwell.io/fhir/end-time')?.valueString ||
    'N/A';
  // Extract Disposition from extension (disposition/data)
  const dispositionExtension = debugCommunication.extension?.find(
    (ext) => ext.url === 'https://projectwell.io/fhir/zoom-web-hook/extensions/zoom-engagement-disposition' && ext.extension
  );
  const dispositionData = dispositionExtension?.extension?.find(
    (ext) => ext.url === 'https://projectwell.io/fhir/zoom-web-hook/extensions/zoom-engagement-disposition-data'
  );
  const dispositionText = dispositionData?.valueCodeableConcept?.text?.split('|')[0] || 'N/A';
  // Extract Notes from note
  const _notes: Annotation[] | undefined = debugCommunication?.note ? debugCommunication.note : undefined;

  const handleCopyClick = async (textToCopy: string) => {
    setIsCopyClickSuccess(false);
    setCopyClickError('');

    try {
      await navigator.clipboard.writeText(textToCopy);

      setIsCopyClickSuccess(true);
    } catch (error: unknown) {
      const errorResponse = ErrorResponse.getMessage(error);
      setCopyClickError(`Failed to copy: ${errorResponse}`);
    }
  };

  const handleAssignClick = (communicationData: CommunicationHelper) => {
    setSelectedCallTicket(communicationData);
    setIsAssignMemberModalOpen(true);
  };

  const handleAssignMember = async (communicationData: CommunicationHelper) => {
    setIsSubmitting(true);

    communicationData.setUknownCallerStatus(false);

    const fhirProxyClient = new FHIRProxyClient(token, `${import.meta.env.VITE_API_ENDPOINT}/fhir/`);

    const actorReference = { reference: `Practitioner/${practitionerId}`, display: practitionerName };
    // `managingOrganization` and its `reference` are both optional and read
    // unguarded here, as is `selectedCallerCard` itself.
    const orgId = selectedCallerCard!.managingOrganization!.reference!.split('/')[1];
    communicationData.setIdentifierOrgId(orgId!);
    const organizationReference = { code: orgId?.toUpperCase(), display: orgId?.toLowerCase() };
    const patientId = selectedCallerCard!.id!;
    const inboundPayload = communicationData.getResource();
    const targetReference = `Communication/${inboundPayload.id}`;

    inboundPayload.subject = { reference: `Patient/${patientId}` };
    inboundPayload.sender = { display: parseFHIRPatientName(selectedCallerCard) };

    const provenance = ProvenanceBuilder.build(
      targetReference,
      actorReference,
      patientId,
      'UPDATE',
      'Zoom communication has been updated to this member',
      organizationReference
    );

    const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([inboundPayload], provenance);
    console.log('inboundPayload', printPrettyJSON(inboundPayload));
    try {
      await fhirProxyClient.send('post', '', fhirBundle);

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
      communicationsMutate();
    } catch (error: unknown) {
      const errorResponse = ErrorResponse.getMessage(error);
      handleErrorMessage(errorResponse);
    } finally {
      setIsSubmitting(false);
      handleCloseModal();
    }
  };

  const handleCloseModal = () => {
    setIsAssignMemberModalOpen(false);
    setSelectedCallTicket(null);
    setSelectedCallerCard(null);
  };

  const handleDeleteCall = async (communicationResource: Communication) => {
    // Change identifier to deleted
    const helper = new CommunicationHelper(communicationResource);

    helper.setState('deleted');
    helper.setSubject(null);
    helper.setUknownCallerStatus(true);

    // Now post bundle request
    setIsSubmitting(true);

    try {
      await fhirPut('/Communication/' + helper.getId(), token, helper.getResource());

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
      communicationsMutate();
      setIsSubmitting(false);
      setDeleteCall(null);
    // Pre-existing swallow, not a new guard: a failed delete leaves the row as it is.
    // eslint-disable-next-line no-empty
    } catch { }
  };

  const handleSelectCallerCard = (patient: Patient) => {
    setSelectedCallerCard(patient);
  };

  const handleRecordsClick = async () => {
    const response = await Connect.connectGet('/hooks/zoom-fetch-call-record/' + id, token);
    setData(response.data);
  };

  const communicationHelper = new CommunicationHelper(debugCommunication);
  const communicationId = communicationHelper.getId();
  const orgId = communicationHelper.getOrgId();

  const [open, setOpen] = useState(false);

  const hasDeleteCall = Boolean(deleteCall);

  return (
    <>
      <Table.Row>
        <Table.Cell>
          {id && (
            <Popup
              trigger={
                <Icon name="copy" onClick={async () => await handleCopyClick(id)} style={{ cursor: 'pointer' }} />
              }
              on="click"
            >
              {!isCopyClickSuccess ? (
                <Loader active inline />
              ) : copyClickError ? (
                <Message content={copyClickError} error />
              ) : (
                <>
                  <Icon name="check" color="green" /> Copied to clipboard
                </>
              )}
            </Popup>
          )}
          {id}
        </Table.Cell>
        <Table.Cell>{sender}</Table.Cell>
        <Table.Cell>{direction}{voicemailIdentifier && `:voicemail`}{transferIdentifier && `:transfer`}</Table.Cell>
        <Table.Cell>
          {channel}:{dispositionText}
          <CommunicationNote
            communicationPayload={debugCommunication}
            orgId={orgId}
            patientId={debugCommunication.subject?.reference?.split('/')[1]}
            refresh={communicationsMutate}
            subject={id || sender}
          />
        </Table.Cell>
        <Table.Cell>
          <Modal
            open={open}
            centered
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            size={typeof data === 'string' ? 'mini' : 'large'}
            style={{ width: typeof data === 'string' ? '300px' : undefined }}
            trigger={
              <Icon
                onClick={() => {
                  setOpen(!open);
                  // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget recording fetch; a rejection leaves the player with no source and no error shown
                  handleRecordsClick();
                }}
                name="play"
                style={{ cursor: 'pointer' }}
                color="blue"
              ></Icon>
            }
            on="click"
            content={
              <>
                {(!data || data === undefined || data === null) && <Loader content="Loading data..." active />}
                {data && <ContentRenderer data={data} />}
                {/* {(!data || data === undefined) && (
                  <Message error content="Oops! Could not find transcript/recording..." />
                )} */}
              </>
            }
          />
        </Table.Cell>
        <Table.Cell>
          {moment
            .utc(startTime)
            .local()
            .format('YYYY-MM-DD hh:mm:ss a')}
        </Table.Cell>
        <Table.Cell>
          {moment
            .utc(endTime)
            .local()
            .format('YYYY-MM-DD hh:mm:ss a')}
        </Table.Cell>
        <Table.Cell>
          <Button color="blue" onClick={() => handleAssignClick(communicationHelper)}>
            Assign
          </Button>
          <Icon
            name="archive"
            color="red"
            style={{ cursor: 'pointer' }}
            onClick={() => setDeleteCall(debugCommunication)}
          />
          <Confirm
            open={hasDeleteCall}
            content="Are you sure you want to archive this communication?"
            cancelButton="Never mind"
            confirmButton={
              <Button color="blue" loading={isSubmitting}>
                Yes, archive it
              </Button>
            }
            onCancel={() => setDeleteCall(null)}
            onConfirm={() => handleDeleteCall(deleteCall!)}
          />
        </Table.Cell>
      </Table.Row>
      {/* Assign Member Modal */}
      {selectedCallTicket?.getId() === communicationId && isAssignMemberModalOpen && (
        <AssignMemberListModal
          handleAssignMember={handleAssignMember}
          handleCloseModal={handleCloseModal}
          handleSelectCallerCard={handleSelectCallerCard}
          isAssignMemberModalOpen={isAssignMemberModalOpen}
          isSubmitting={isSubmitting}
          orgId={orgId!}
          selectedCallTicket={selectedCallTicket}
          selectedCallerCard={selectedCallerCard}
        />
      )}
    </>
  );
};

export default ZoomRow;

function ContentRenderer({ data }: { data: ZoomCallRecord }) {
  return (
    <>
      {Array.isArray(data) && (
        <>
          <div style={{ maxHeight: 400, overflowY: 'auto', padding: '1rem' }}>
            {data.map((line, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: '1rem',
                  borderBottom: '1px solid #ddd',
                  paddingBottom: '0.5rem',
                }}
              >
                {line ? <div>{line}</div> : <div style={{ fontStyle: 'italic', color: '#999' }}>[empty]</div>}
              </div>
            ))}
          </div>
        </>
      )}
      {typeof data === 'object' && !Array.isArray(data) && (
        <>
          {/* `typeof null === 'object'`, so this branch admits `null`; see `ZoomCallRecord`. */}
          <RenderEmail data={data!} />
        </>
      )}
      {typeof data === 'string' && (
        <>
          <AudioIconPopupZoom data={data} />
        </>
      )}
    </>
  );
}

function RenderEmail({ data }: { data: { messages?: ZoomEmailMessage[] } }) {
  return (
    <div style={{ maxHeight: 400, overflowY: 'auto', padding: '1rem' }}>
      {data.messages!.map((msg: ZoomEmailMessage, idx: number) => (
        <div key={idx} style={{ marginBottom: '1rem', borderBottom: '1px solid #ddd', paddingBottom: '0.5rem' }}>
          <div style={{ fontWeight: 'bold' }}>{msg.user_name}</div>
          {/*
            SUSPECTED DEFECT (a message with no `sent_time` renders "Invalid Date") —
            REPORTED, NOT FIXED. `sent_time` is optional on the Zoom webhook payload,
            and `new Date(undefined)` is an Invalid Date whose `toLocaleString()` is
            the literal string "Invalid Date", shown to the user in the timestamp
            slot. The assertion keeps that exact output; suppressing the row or
            substituting a date would both change what the care team sees.
          */}
          <div style={{ fontSize: '0.8em', color: '#555' }}>{new Date(msg.sent_time!).toLocaleString()}</div>

          {msg.content ? (
            <div style={{ marginTop: '0.5rem' }} dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(msg.content) }} />
          ) : (
            <div style={{ marginTop: '0.5rem' }}>{msg.text}</div>
          )}

          {msg.inlineFiles && msg.inlineFiles.length > 0 && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.8em', color: '#777' }}>
              <strong>Attachments:</strong>
              <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                {msg.inlineFiles.map((f: ZoomEmailInlineFile, i: number) => (
                  <li key={i}>
                    {f.file_name} ({f.file_type})
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
